import { FunctionComponent, memo } from "react";
import {
    Page,
    PageContent,
    PageHeader,
    PageSidebar,
    PageSidebarContent,
    PageSidebarHeader,
} from "@edgetier/client-components";
import { IProps } from "./agent-groups.types";
import AgentSelectionContainer from "./agent-selection-container";
import TagsList from "pages/tag-phrase-triggers/tags-list";
import { ITag, Permission, TagGroupType } from "@edgetier/watchtower-types";
import PageRoute from "constants/page";
import { useParams } from "react-router-dom";
import { useTag } from "hooks-for/tags/use-tag";
import { SpinnerUntil } from "@edgetier/components";
import useAgents from "hooks-for/agents/use-agents";
import "./agent-groups.scss";

/**
 * A screen for viewing and editing a agent groups and assigning
 * agents to groups
 */
const AgentGroups: FunctionComponent<IProps> = () => {
    const { tagId } = useParams<{ readonly tagId: string | undefined }>();

    // Get tag details
    const {
        data: tag,
        isLoading: isTagLoading,
        isError: hasTagError,
    } = useTag(tagId!, {
        enabled: typeof tagId === "string",
    });

    // Get all agents
    const {
        data: allAgents,
        isLoading: areAllAgentsLoading,
        isError: hasAllAgentsError,
    } = useAgents({}, { enabled: typeof tagId === "string" });

    // Get all agents with this tag
    const {
        data: groupAgents,
        isLoading: areGroupAgentsLoading,
        isError: hasGroupAgentsError,
    } = useAgents({ tagId: [tagId ?? ""] }, { enabled: typeof tagId === "string" });

    return (
        <>
            <PageSidebar>
                <PageSidebarHeader title="Agent Groups" />
                <PageSidebarContent>
                    <TagsList
                        tagGroupType={TagGroupType.Agent}
                        showExploreAction={false}
                        redirectRoute={PageRoute.AgentGroups}
                        actionPermissions={Permission.ManageAgentTags}
                        showTagGroupUnaryForAgents
                    />
                </PageSidebarContent>
            </PageSidebar>
            <Page className="agent-groups">
                <PageHeader
                    title={
                        isTagLoading
                            ? "...loading"
                            : typeof tag === "undefined"
                            ? "Agents"
                            : `${tag.tagGroup} / ${tag?.tag} / Agents`
                    }
                />
                <PageContent>
                    <SpinnerUntil<ITag>
                        isReady={!isTagLoading && !areAllAgentsLoading && !areGroupAgentsLoading}
                        data={tag}
                    >
                        {hasTagError || hasAllAgentsError || hasGroupAgentsError ? (
                            <div className="agent-groups__error">An error occured</div>
                        ) : (
                            <>
                                {typeof tag !== "undefined" && tag !== null ? (
                                    <AgentSelectionContainer
                                        key={tag.tagId}
                                        tagId={tag.tagId}
                                        allAgents={allAgents ?? []}
                                        groupAgents={groupAgents ?? []}
                                    />
                                ) : (
                                    <div className="agent-groups__no-tag-selected">
                                        Please select a tag group to assign agents
                                    </div>
                                )}
                            </>
                        )}
                    </SpinnerUntil>
                </PageContent>
            </Page>
        </>
    );
};

export default memo(AgentGroups);
